import { useFlags } from '@atlaskit/flag';
import TextField from '@atlaskit/textfield';
import { ILocationStatus } from 'core/api/meta/meta-api-interface';
import MetaApiService from 'core/api/meta/meta-api.service';
import { ButtonType } from 'core/enums/button-type';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useState } from 'react';
import SharedButton from 'shared/components/buttons/button';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { ISharedDialogButton } from 'shared/components/dialog-base/dialog-base-interface';
import { v4 as uuid } from 'uuid';

interface IEditLocationStatusDialog {
  status: ILocationStatus[];
}

const EditLocationStatusDialog = ({ status }: IEditLocationStatusDialog) => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editedStatus, setEditedStatus] = useState<ILocationStatus[]>(status);

  const flags = useFlags();
  const dialog = useDialog();

  const submit = async () => {
    try {
      setFormSubmitting(true);
      if (editedStatus.some((status) => status.name.trim() === '')) {
        throw new Error('Status name cannot be empty');
      }
      await MetaApiService.update('deliveries', {
        locationStatus: editedStatus,
      });
      dialog?.closeDialog();
      showSuccessFlag('Update successful', 'Location status updated successfully', flags);
    } catch (error) {
      setFormSubmitting(false);
      showErrorFlag('Update failed', 'Unable to update the location status, please try again', flags);
    }
  };

  const buttons: ISharedDialogButton[] = [
    {
      key: 'close',
      type: ButtonType.Button,
      buttonProps: {
        onClick: () => dialog?.closeDialog(),
        label: 'Cancel',
        appearance: 'subtle',
        type: 'button',
      },
    },
    {
      key: 'submit',
      type: ButtonType.Button,
      buttonProps: {
        isLoading: formSubmitting,
        onClick: submit,
        label: 'Save',
        appearance: 'primary',
        type: 'button',
      },
    },
  ];

  const customContent = () => {
    return (
      <div className='p-4 flex flex-col gap-4 overflow-y-auto'>
        {editedStatus.map((status) => (
          <div key={status.key} className='flex items-center gap-2'>
            <TextField
              isDisabled={formSubmitting}
              value={status.name}
              onChange={(event) => {
                const newValue = event.currentTarget.value;
                setEditedStatus((prev) => prev.map((s) => (s.key === status.key ? { ...s, name: newValue } : s)));
              }}
            />
            {editedStatus.length > 1 && status.key !== 'other' && (
              <SharedButton
                disabled={formSubmitting}
                onClick={() => setEditedStatus((prev) => prev.filter((s) => s.key !== status.key))}
                type='button'
                appearance='danger'
                label='Remove'
              />
            )}
          </div>
        ))}
        <SharedButton
          disabled={formSubmitting}
          onClick={() =>
            setEditedStatus((prev) => {
              prev.splice(prev.length - 1, 0, {
                key: uuid(),
                name: 'New status',
                active: true,
              });
              return [...prev];
            })
          }
          type='button'
          appearance='primary'
          label='Add Status'
        />
      </div>
    );
  };

  return (
    <SharedDialogBase title='Edit location status' customContentTemplate={customContent()} customButtons={buttons} />
  );
};

export default EditLocationStatusDialog;
